import {useCallback, useMemo} from 'react';
import {useQueryClient, UseQueryResult} from 'react-query';
import {FeaturePointerVO, LayerVO, ServicesApi} from '../../api';
import {DataRequestVO, DataResponseVO} from '@mc/data-filter-react';
import {GroupFeaturePointer} from '@mc/react-mapio';
import {useMapioContext} from '../../mapioContext';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {useAppConfig} from '@mc/react-app-base';
import {CACHE_KEY_VIEWPORT_FEATURE} from '../../queryKeys';
import {ViewportResult} from './searchPanelTypes';
import {mapFeatureCardToViewportResult} from '../../helpers/featureCard';

type UseViewportFeaturesParams = {
    /** Pointery objektů ve výřezu (s layerId) z eventu EXTENT_FEATURES, režim "všechny zapnuté vrstvy". */
    extentFeaturePointers?: GroupFeaturePointer[];
    /** Zda se má výřezový seznam vůbec načítat (panel otevřený a bez zadaného hledání). */
    enabled: boolean;
};

/** Composite klíč řádku (`layerId:featureId`) → zpět na dvojici. LayerId je číselné, první ':' je oddělovač. */
const parseRowId = (rowId: string): {layerId: string; featureId: string} => {
    const sep = rowId.indexOf(':');
    return {layerId: rowId.slice(0, sep), featureId: rowId.slice(sep + 1)};
};

/**
 * Datový zdroj výřezového seznamu pro ui-v4 VirtualList (líné okénkové načítání + virtualizovaný render).
 *
 * Nevrací hotová data: vrací seznam `ids` (composite `layerId:featureId` v pořadí od enginu) a `api`
 * callback, kterým si VirtualList dotahuje data JEN pro okno viditelných řádků – ve výřezu tak mohou být
 * i tisíce prvků, aniž by se hromadně stahovaly nebo vykreslovaly. Pro okno se volá:
 *  - `ServicesApi.getFeatureCards` JEDINÝM batchem přes všechny vrstvy – vrací hodnoty objektů (jako
 *    getDataByIds), kódy přístupnosti i ID první fotky. Definice atributů se už netahají zvlášť
 *    (žádné getLayerAttributes). Vrstvové zobrazení (ikona/barva) bere klient z konfigurace vrstev.
 */
export const useViewportFeatures = ({extentFeaturePointers, enabled}: UseViewportFeaturesParams) => {
    const mapioContext = useMapioContext();
    const {topicId, context} = mapioContext.value;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    const queryClient = useQueryClient();
    // @ts-ignore
    const {serverContextPath} = useAppConfig();

    // Composite id řádků v pořadí od enginu; stabilní identita přes join-klíč (pole chodí nové při každém eventu).
    const idsKey = (extentFeaturePointers ?? []).map(p => `${p.layerId}:${p.featureId}`).join(',');
    const ids = useMemo(
        () => (enabled && idsKey ? idsKey.split(',') : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [enabled, idsKey],
    );

    // Cache načteného objektu držíme přímo v react-query (klíč = composite id + téma/kontext). Přežívá změny
    // `ids` (posun/zoom mapy vyvolá nový EXTENT_FEATURES), takže při hoveru zazoomovaný objekt ani jeho sousedé
    // se znovu ze serveru netahají. O paměť (garbage collection nepozorovaných dat) i dedup se stará react-query.
    const featureCacheKey = useCallback((rowId: string) => [CACHE_KEY_VIEWPORT_FEATURE, topicId, context ?? '', rowId], [topicId, context]);

    /** Načte data pro okno viditelných řádků (volá VirtualList přes idsRequest). */
    const api = useCallback(
        async (request: DataRequestVO<object>): Promise<DataResponseVO<ViewportResult>> => {
            const windowIds = (request.idsRequest?.ids ?? []) as unknown as string[];
            const layersConfig = layersConfigQuery.data ?? {};
            if (windowIds.length === 0) {
                return {rows: [], totalElements: 0, totalDataElements: 0};
            }

            // Dotahujeme jen objekty, které v cache (react-query) ještě nejsou (klíčem je composite id).
            const missingIds = windowIds.filter(id => !queryClient.getQueryData<ViewportResult>(featureCacheKey(id)));
            if (missingIds.length === 0) {
                const cachedRows = windowIds
                    .map(id => queryClient.getQueryData<ViewportResult>(featureCacheKey(id)))
                    .filter((r): r is ViewportResult => Boolean(r));
                return {rows: cachedRows, totalElements: cachedRows.length, totalDataElements: cachedRows.length};
            }

            // JEDINÝ batch dotaz přes všechny vrstvy – hodnoty objektů (jako getDataByIds) + ID první fotky.
            const pointers: FeaturePointerVO[] = missingIds.map(rowId => {
                const {layerId, featureId} = parseRowId(rowId);
                return {featureId, layerId: Number(layerId)};
            });
            let cards: Awaited<ReturnType<typeof ServicesApi.getFeatureCards>>['data'] = [];
            try {
                cards = (await ServicesApi.getFeatureCards(topicId, context ?? undefined, pointers)).data ?? [];
            } catch {
                cards = [];
            }

            const newRows: ViewportResult[] = cards.map(card => mapFeatureCardToViewportResult(card, layersConfig, serverContextPath));

            // Ulož nově načtené do cache (react-query); staleTime Infinity = data objektu se nemění za běhu.
            newRows.forEach(r => queryClient.setQueryData(featureCacheKey(r.id), r));

            // Výsledek okna skládáme z cache v pořadí `windowIds` (zachová pořadí od enginu).
            const rows = windowIds.map(id => queryClient.getQueryData<ViewportResult>(featureCacheKey(id))).filter((r): r is ViewportResult => Boolean(r));
            return {rows, totalElements: rows.length, totalDataElements: rows.length};
        },
        [layersConfigQuery.data, context, topicId, serverContextPath, queryClient, featureCacheKey],
    );

    return {ids, api, isReady: !!layersConfigQuery.data};
};
